import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Hex } from 'viem';
import { LobbyController } from '../../services/lobby-controller';
import { Wallet } from '../../services/wallet';
import {
  DirectChallengeRequest,
  OnlinePlayerRowComponent,
} from '../online-player-row/online-player-row.component';
import { PixelCreatureComponent } from '../pixel-creature/pixel-creature.component';
import { SheetDrag } from '../../utils/swipe';

const readLayoutToken = (name: string) =>
  parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;

@Component({
  selector: 'app-online-players-sheet',
  imports: [OnlinePlayerRowComponent, PixelCreatureComponent],
  templateUrl: './online-players-sheet.component.html',
  styleUrl: './online-players-sheet.component.css',
  host: { '[style.--sheet-peek-inset]': 'peekInsetCss()' },
})
export class OnlinePlayersSheetComponent {
  private readonly wallet = inject(Wallet);

  readonly challengePending = input<Hex | null>(null);
  readonly defaultMode = input(0);
  // Height (px) of fixed bottom chrome the sheet must clear so `--sheet-peek`
  // stays the visible band. Home's bottom nav covers `--mobile-nav-clearance`;
  // home2 has no nav, so it passes 0.
  readonly bottomInset = input(readLayoutToken('--mobile-nav-clearance'));

  readonly challenge = output<DirectChallengeRequest>();

  readonly expanded = signal(false);
  readonly openAddress = signal<Hex | null>(null);

  private readonly sheetRef = viewChild.required<ElementRef<HTMLElement>>('sheetEl');
  private readonly scrimRef = viewChild.required<ElementRef<HTMLElement>>('scrimEl');

  protected readonly peekInsetCss = computed(() => `${this.bottomInset()}px`);

  // Collapsed detent height: the visible peek plus whatever bottom chrome hides it.
  private readonly peekTotalPx = computed(
    () => readLayoutToken('--sheet-peek') + this.bottomInset(),
  );

  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  // Pull-up detent sheet: SheetDrag owns the gesture; this maps progress to the
  // sheet's translateY + scrim opacity and animates the settle.
  protected readonly sheetDrag = new SheetDrag({
    openDir: 'up',
    isOpen: () => this.expanded(),
    measureTravel: () =>
      this.sheetRef().nativeElement.getBoundingClientRect().height - this.peekTotalPx(),
    onStart: () => {
      if (this.settleTimer) {
        clearTimeout(this.settleTimer);
        this.settleTimer = null;
        this.clearInlineStyles();
      }
    },
    onEngage: () => {
      this.sheetRef().nativeElement.style.transition = 'none';
      this.scrimRef().nativeElement.style.transition = 'none';
    },
    onDrag: (progress, travel) => {
      // progress 1 = open (translateY 0), 0 = collapsed (translateY travel).
      this.sheetRef().nativeElement.style.transform = `translateY(${(1 - progress) * travel}px)`;
      this.scrimRef().nativeElement.style.opacity = String(progress);
    },
    onSettle: (open, engaged, travel) => {
      if (!engaged) {
        this.expanded.update((v) => !v);
        return;
      }
      // Animate to the detent inline (transition re-enabled), then hand the
      // resting state back to the CSS classes once it settles.
      const sheet = this.sheetRef().nativeElement;
      const scrim = this.scrimRef().nativeElement;
      sheet.style.transition = '';
      scrim.style.transition = '';
      sheet.style.transform = open ? 'translateY(0px)' : `translateY(${travel}px)`;
      scrim.style.opacity = open ? '1' : '0';
      this.expanded.set(open);
      this.settleTimer = setTimeout(() => {
        this.settleTimer = null;
        this.clearInlineStyles();
      }, 350);
    },
    onCancel: () => this.clearInlineStyles(),
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.sheetDrag.destroy();
    });
  }

  readonly otherPlayers = inject(LobbyController).otherPlayers;

  readonly onlineCount = computed(
    () => this.otherPlayers().length + (this.wallet.walletAddress() ? 1 : 0),
  );

  readonly headAvatars = computed(() =>
    this.otherPlayers().slice(0, 4).map((entry) => entry.address),
  );

  toggleRow(address: Hex): void {
    this.openAddress.update((current) =>
      current === address ? null : address,
    );
  }

  collapse(): void {
    this.expanded.set(false);
  }

  // Body rows are pointer-events:none while collapsed, so a tap over them
  // lands on the sheet itself — expand instead of acting on a row.
  onSheetClick(event: Event): void {
    if (this.expanded()) return;
    if ((event.target as HTMLElement).closest('.grab')) return;
    this.expanded.set(true);
  }

  private clearInlineStyles(): void {
    const sheet = this.sheetRef().nativeElement;
    const scrim = this.scrimRef().nativeElement;
    sheet.style.transition = '';
    sheet.style.transform = '';
    scrim.style.transition = '';
    scrim.style.opacity = '';
  }
}
